import {
  AlignmentType,
  Document,
  Footer,
  Header,
  ImageRun,
  Paragraph,
  TextRun,
} from 'docx';

// Types
import { TemplateProps } from '../../type';

// Utils
import { loadFontFaces, PROXIMA_NOVA_FONT_FACES } from '../../utils/fonts';
import { fetchImageAsBuffer } from '../../utils/image';

// Constants
import {
  COLORS,
  FONT_FAMILY,
  FONT_SIZE,
  LOGO_HEIGHT_PX,
  LOGO_WIDTH_PX,
  SPACINGS,
  US_LETTER,
} from './constants';

// Tokens
import { CONFIDENTIALITY_NOTICE } from '../tokens';

// Components
import { PersonalInfoSection } from './components/PersonalInfo';
import { TechnicalSkillsSection } from './components/TechnicalSkills';
import { ExperiencesSection } from './components/Experiences';
import { EducationSection } from './components/Education';
import { CertificatesSection } from './components/Certificates';

export const generateAgilityIODocx = async (
  data: TemplateProps['data'],
): Promise<Document> => {
  const { info, technicalSkills, journeys, educations, certs } = data ?? {};
  const { name = '', title = '', address = '', bio = '' } = info ?? {};

  const [fonts, logoData] = await Promise.all([
    loadFontFaces(PROXIMA_NOVA_FONT_FACES),
    fetchImageAsBuffer('/images/agility-logo.png'),
  ]);

  const headerChildren: Paragraph[] = logoData
    ? [
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          children: [
            new ImageRun({
              data: logoData,
              transformation: {
                width: LOGO_WIDTH_PX,
                height: LOGO_HEIGHT_PX,
              },
              type: 'png',
              altText: {
                title: 'Logo',
                description: 'Company logo',
                name: 'logo',
              },
            }),
          ],
        }),
      ]
    : [];

  return new Document({
    fonts,
    styles: {
      default: {
        document: {
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZE.base,
            color: COLORS.foreground,
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            size: {
              width: US_LETTER.width,
              height: US_LETTER.height,
            },
            margin: {
              top: SPACINGS.pagePadding,
              bottom: SPACINGS.pagePadding,
              left: SPACINGS.pagePadding,
              right: SPACINGS.pagePadding,
            },
          },
        },
        headers: {
          default: new Header({ children: headerChildren }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: CONFIDENTIALITY_NOTICE,
                    font: FONT_FAMILY,
                    size: FONT_SIZE.base,
                    color: COLORS.muted,
                  }),
                ],
              }),
            ],
          }),
        },
        children: [
          ...PersonalInfoSection(name, title, address, bio),
          ...TechnicalSkillsSection(technicalSkills),
          ...ExperiencesSection(journeys),
          ...EducationSection(educations),
          ...CertificatesSection(certs),
        ],
      },
    ],
  });
};
